import React from "react"
import { DATASETS } from "./constants"

const headerStyle = {
  padding: "8px 12px",
  backgroundColor: "#407775",
  color: "#fff",
  fontSize: "11px",
  fontWeight: "600",
  textAlign: "left",
  whiteSpace: "nowrap",
}

const cellStyle = (rowIndex) => ({
  padding: "7px 12px",
  color: "#494949",
  fontSize: "12px",
  backgroundColor: rowIndex % 2 === 0 ? "#FCF7EE" : "#DAECEC",
})

const BoolCell = ({ value, rowIndex }) => {
  const isYes = value === true || value === "Yes"
  return (
    <td style={cellStyle(rowIndex)}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: isYes ? "#E8F5E9" : "#FFEBEE",
          fontSize: "11px",
        }}
      >
        {isYes ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#C62828" strokeWidth="3">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
      </span>
    </td>
  )
}

const FRAMEWORK_COLORS = {
  BRSR: { bg: "#E3F2FD", text: "#1565C0" },
  GRI:  { bg: "#E8F5E9", text: "#2E7D32" },
  TCFD: { bg: "#FFF3E0", text: "#E65100" },
  SBTi: { bg: "#F3E5F5", text: "#6A1B9A" },
  CDP:  { bg: "#E0F2F1", text: "#004D40" },
}

const FrameworkPill = ({ framework }) => {
  const c = FRAMEWORK_COLORS[framework] || { bg: "#F5F5F5", text: "#666" }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 7px",
        borderRadius: "4px",
        backgroundColor: c.bg,
        color: c.text,
        fontSize: "10px",
        fontWeight: "600",
        margin: "1px 2px",
        whiteSpace: "nowrap",
      }}
    >
      {framework}
    </span>
  )
}

const ESRS_CODES = ["E1", "E2", "E3", "E4", "E5", "S1", "S2", "S3", "S4", "G1"]

const parseEsrsString = (esrsString) => {
  if (!esrsString) return {}
  const mentioned = esrsString.toUpperCase().replace(/\s/g, "")
  const result = {}
  for (const code of ESRS_CODES) {
    result[code] = mentioned.includes(code)
  }
  return result
}

const EsrsTable = ({ data }) => {
  if (!data || data.length === 0) return null

  return (
    <div
      style={{
        border: "1px solid #E5E5E5",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#fff",
        marginBottom: "12px",
      }}
    >
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #E5E5E5" }}>
        <h4 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#494949" }}>
          ESRS Standards Mentioned
        </h4>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerStyle}>Company</th>
              {ESRS_CODES.map((code) => (
                <th key={code} style={headerStyle}>{code}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const parsed = parseEsrsString(row.esrs)
              return (
                <tr key={i}>
                  <td style={{ ...cellStyle(i), fontWeight: "500" }}>{row.company}</td>
                  {ESRS_CODES.map((code) => (
                    <BoolCell key={code} value={parsed[code]} rowIndex={i} />
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const KNOWN_FRAMEWORKS = ["BRSR", "GRI", "TCFD", "SBTi", "CDP"]

const AlignmentTable = ({ data, dataset = DATASETS.CSRD }) => {
  if (!data || data.length === 0) return null
  const isNiftyLike = dataset === DATASETS.NIFTY || dataset === DATASETS.ALL

  return (
    <div
      style={{
        border: "1px solid #E5E5E5",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #E5E5E5" }}>
        <h4 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#494949" }}>
          Framework Alignment
        </h4>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerStyle}>Company</th>
              {isNiftyLike ? (
                <>
                  <th style={headerStyle}>Frameworks</th>
                  <th style={headerStyle}>SBTi Committed</th>
                  <th style={headerStyle}>Net Zero Year</th>
                  <th style={headerStyle}>ESG Rating</th>
                  <th style={headerStyle}>Source Report</th>
                </>
              ) : (
                <>
                  <th style={headerStyle}>GRI Aligned</th>
                  <th style={headerStyle}>SBTi Target</th>
                  <th style={headerStyle}>TCFD Referenced</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                <td style={{ ...cellStyle(i), fontWeight: "500" }}>{row.company}</td>
                {isNiftyLike ? (
                  <>
                    <td style={cellStyle(i)}>
                      {(row.frameworks || [])
                        .filter((f) => KNOWN_FRAMEWORKS.includes(f))
                        .map((f) => <FrameworkPill key={f} framework={f} />)}
                      {(row.frameworks || []).filter((f) => KNOWN_FRAMEWORKS.includes(f)).length === 0 && (
                        <span style={{ color: "#999", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                    <td style={cellStyle(i)}>
                      {row.sbti_committed ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#2E7D32", fontSize: "11px", fontWeight: "600" }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                          Committed
                        </span>
                      ) : (
                        <span style={{ color: "#999", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                    <td style={cellStyle(i)}>
                      {row.net_zero_target_year != null ? (
                        <span style={{ fontWeight: "600", color: "#407775" }}>{row.net_zero_target_year}</span>
                      ) : (
                        <span style={{ color: "#999", fontSize: "11px" }}>No target set</span>
                      )}
                    </td>
                    <td style={cellStyle(i)}>
                      {row.esg_rating != null ? (
                        <span style={{ fontWeight: "500" }}>{row.esg_rating}</span>
                      ) : (
                        <span style={{ color: "#999", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                    <td style={cellStyle(i)}>
                      {(row.sustainability_report_url || row.report_url) ? (
                        <a
                          href={row.sustainability_report_url || row.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-block",
                            padding: "3px 8px",
                            borderRadius: "4px",
                            border: "1px solid #407775",
                            color: "#407775",
                            fontSize: "10px",
                            fontWeight: "600",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          View Report
                        </a>
                      ) : (
                        <span style={{ color: "#999", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <BoolCell value={row.gri_alignment} rowIndex={i} />
                    <BoolCell value={row.sbti_target_mentioned} rowIndex={i} />
                    <BoolCell value={row.tcfd_referenced} rowIndex={i} />
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const FrameworkTable = ({ esrsData, alignmentData, dataset = DATASETS.CSRD }) => {
  return (
    <>
      {dataset !== DATASETS.NIFTY && <EsrsTable data={esrsData} />}
      <AlignmentTable data={alignmentData} dataset={dataset} />
    </>
  )
}

export default FrameworkTable
